import { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { ChevronDown, Mic } from 'lucide-react';
import { ai as aiApi } from '../utils/api';
import useIsMobile from '../hooks/useIsMobile';

const CARD_SHADOW = '0 6px 18px rgba(35,38,47,0.08)';
const tint = (c, pct) => `color-mix(in srgb, ${c} ${pct}%, transparent)`;

// ── Costanti ──────────────────────────────────────────────────────────────────

const STATI_LABEL = {
  ricevuto:            { label: 'Ricevuto',    color: 'var(--ds-muted)' },
  trascrizione:        { label: 'Trascrizione',color: 'var(--zone-blue)' },
  parsing:             { label: 'Parsing',     color: 'var(--zone-amber)' },
  in_attesa_risposta:  { label: 'In Attesa',   color: 'var(--warn)' },
  completato:          { label: 'Completato',  color: 'var(--brand)' },
  fallito:             { label: 'Fallito',     color: 'var(--ds-danger)' },
};

const INTENT_LABEL = {
  create_cantiere:     'Nuovo Cantiere',
  update_cantiere:     'Aggiorna Cantiere',
  close_cantiere:      'Chiudi Cantiere',
  create_fattura:      'Nuova Fattura',
  create_preventivo:   'Nuovo Preventivo',
  create_cliente:      'Nuovo Cliente',
  log_costo:           'Costo',
  set_reminder:        'Promemoria',
  create_appuntamento: 'Appuntamento',
  query_fatture:       'Query Fatture',
  query_preventivi:    'Query Preventivi',
  query_cantiere:      'Query Cantiere',
  query_kpi:           'Query KPI',
  send_sollecito:      'Sollecito',
  report_giornaliero:  'Report Giornaliero',
  multi_intent:        'Multi-Intent',
  unknown:             'Sconosciuto',
};

const INTENT_COLOR = {
  create_cantiere:     'var(--brand)',
  update_cantiere:     'var(--zone-blue)',
  close_cantiere:      'var(--ds-danger)',
  create_fattura:      'var(--warn)',
  create_preventivo:   'var(--zone-amber)',
  create_cliente:      'var(--zone-blue)',
  log_costo:           'var(--zone-amber)',
  set_reminder:        'var(--zone-violet)',
  create_appuntamento: 'var(--ok)',
  query_fatture:       'var(--zone-blue)',
  query_preventivi:    'var(--zone-amber)',
  query_cantiere:      'var(--zone-acqua)',
  query_kpi:           'var(--zone-violet)',
  send_sollecito:      'var(--warn)',
  report_giornaliero:  'var(--ok)',
  multi_intent:        'var(--zone-magenta)',
  unknown:             'var(--ds-muted)',
};

const STATI_ATTIVI = ['ricevuto', 'trascrizione', 'parsing', 'in_attesa_risposta'];

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtLatenza(ms) {
  if (!ms) return '—';
  return ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${ms}ms`;
}

function fmtData(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('it-IT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

function trunc(s, n = 80) {
  if (!s) return '—';
  return s.length > n ? s.slice(0, n) + '…' : s;
}

// ── Badge component ───────────────────────────────────────────────────────────

function Badge({ label, color }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '2px 8px', borderRadius: 20,
      background: tint(color, 14), color,
      fontSize: 11, fontWeight: 500, whiteSpace: 'nowrap',
    }}>
      {label}
    </span>
  );
}

// ── KPI Card ──────────────────────────────────────────────────────────────────

function KpiCard({ label, value, color, sub }) {
  return (
    <div style={{
      background: 'var(--ds-bg)', border: '1px solid var(--ds-border)',
      borderRadius: 'var(--radius-card)', boxShadow: CARD_SHADOW, padding: '16px 20px', flex: 1, minWidth: 0,
    }}>
      <div style={{ fontSize: 11, color: 'var(--ds-muted)', fontWeight: 500, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
        {label}
      </div>
      <div style={{ fontSize: 24, fontWeight: 500, fontFamily: 'var(--font-display)', color: color || 'var(--ink)' }}>
        {value ?? '—'}
      </div>
      {sub && <div style={{ fontSize: 11, color: 'var(--ds-muted)', marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

// ── Riga task espandibile ─────────────────────────────────────────────────────

function RispondiForm({ taskId, onSuccess }) {
  const [risposta, setRisposta] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  const handleInvia = async () => {
    if (!risposta.trim()) return;
    setSending(true);
    setError('');
    try {
      await aiApi.rispondi(taskId, risposta.trim());
      setRisposta('');
      onSuccess();
    } catch (err) {
      const msg = err.response?.data?.errore || 'Errore nell\'invio della risposta';
      setError(msg);
    } finally {
      setSending(false);
    }
  };

  return (
    <div style={{
      marginTop: 12, padding: '12px 14px', borderRadius: 'var(--radius-base)',
      background: tint('var(--brand)', 6), border: '1px solid ' + tint('var(--brand)', 25),
    }}>
      <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--brand)', marginBottom: 8, textTransform: 'uppercase' }}>
        Rispondi dalla piattaforma
      </div>
      <textarea
        value={risposta}
        onChange={e => setRisposta(e.target.value)}
        placeholder="Scrivi la tua risposta..."
        disabled={sending}
        style={{
          width: '100%', minHeight: 60, padding: '8px 12px', borderRadius: 'var(--radius-base)',
          border: '1px solid var(--ds-border)', background: 'var(--ds-bg)',
          fontSize: 13, fontFamily: 'inherit', color: 'var(--ds-text)',
          resize: 'vertical',
        }}
      />
      {error && (
        <div style={{ fontSize: 12, color: 'var(--ds-danger)', marginTop: 4 }}>{error}</div>
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
        <button
          onClick={handleInvia}
          disabled={sending || !risposta.trim()}
          style={{
            padding: '8px 20px', borderRadius: 'var(--radius-base)', border: 'none',
            background: sending || !risposta.trim() ? 'var(--ds-border)' : 'var(--brand)',
            color: sending || !risposta.trim() ? 'var(--ds-muted)' : '#fff',
            fontSize: 13, fontWeight: 500, cursor: sending ? 'wait' : 'pointer',
          }}
        >
          {sending ? 'Invio in corso...' : 'Invia risposta'}
        </button>
      </div>
    </div>
  );
}

function TaskRow({ task, expanded, onToggle, onRisposta }) {
  const stato = STATI_LABEL[task.stato] || { label: task.stato, color: 'var(--ds-muted)' };

  return (
    <>
      <tr
        className="table-row"
        onClick={onToggle}
        style={{ cursor: 'pointer', userSelect: 'none' }}
      >
        {/* Stato */}
        <td style={{ padding: '10px 12px' }}>
          <Badge label={stato.label} color={stato.color} />
        </td>

        {/* Intent */}
        <td style={{ padding: '10px 12px' }}>
          {task.intent_riconosciuto
            ? <Badge label={INTENT_LABEL[task.intent_riconosciuto] || task.intent_riconosciuto} color={INTENT_COLOR[task.intent_riconosciuto] || 'var(--ds-muted)'} />
            : <span style={{ color: 'var(--ds-muted)', fontSize: 12 }}>—</span>
          }
        </td>

        {/* Trascrizione */}
        <td style={{ padding: '10px 12px', fontSize: 12, color: 'var(--ds-text)', maxWidth: 280 }}>
          {trunc(task.trascrizione)}
        </td>

        {/* Utente */}
        <td style={{ padding: '10px 12px', fontSize: 12, color: 'var(--ds-muted)' }}>
          {task.utente_email ? task.utente_email.split('@')[0] : '—'}
        </td>

        {/* Latenza */}
        <td style={{ padding: '10px 12px', fontSize: 12, textAlign: 'right', color: 'var(--ds-muted)' }}>
          {fmtLatenza(task.latenza_totale_ms)}
        </td>

        {/* Data */}
        <td style={{ padding: '10px 12px', fontSize: 12, color: 'var(--ds-muted)', whiteSpace: 'nowrap' }}>
          {fmtData(task.created_at)}
        </td>

        {/* Espandi */}
        <td style={{ padding: '10px 12px', textAlign: 'center' }}>
          <ChevronDown size={14} strokeWidth={2} color="var(--ds-muted)"
            style={{ transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }} />
        </td>
      </tr>

      {/* Riga dettaglio espansa */}
      {expanded && (
        <tr style={{ background: 'var(--chip)' }}>
          <td colSpan={7} style={{ padding: '12px 20px 16px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>

              {/* Trascrizione completa */}
              <div>
                <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--ds-muted)', marginBottom: 4, textTransform: 'uppercase' }}>
                  Trascrizione
                </div>
                <div style={{
                  background: 'var(--ds-bg)', border: '1px solid var(--ds-border)',
                  borderRadius: 'var(--radius-base)', padding: '10px 12px', fontSize: 13, color: 'var(--ds-text)',
                  lineHeight: 1.5, minHeight: 40,
                }}>
                  {task.trascrizione || <em style={{ color: 'var(--ds-muted)' }}>Nessuna trascrizione</em>}
                </div>
              </div>

              {/* Intent params */}
              <div>
                <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--ds-muted)', marginBottom: 4, textTransform: 'uppercase' }}>
                  Parametri estratti
                </div>
                <div style={{
                  background: 'var(--ds-bg)', border: '1px solid var(--ds-border)',
                  borderRadius: 'var(--radius-base)', padding: '10px 12px', fontSize: 12, fontFamily: 'monospace',
                  color: 'var(--ds-text)', lineHeight: 1.6, minHeight: 40,
                  whiteSpace: 'pre-wrap', wordBreak: 'break-all',
                }}>
                  {task.intent_params && Object.keys(task.intent_params).length > 0
                    ? JSON.stringify(task.intent_params, null, 2)
                    : <em style={{ fontFamily: 'inherit', color: 'var(--ds-muted)' }}>—</em>
                  }
                </div>
              </div>

            </div>

            {/* Conversazione multi-step */}
            {task.stato_conversazione && task.stato_conversazione !== 'nessuno' && (
              <div style={{
                marginTop: 12, padding: '10px 14px', borderRadius: 'var(--radius-base)',
                background: tint('var(--zone-amber)', 6), border: '1px solid ' + tint('var(--zone-amber)', 20),
              }}>
                <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--zone-amber)', marginBottom: 6, textTransform: 'uppercase' }}>
                  Conversazione · {task.stato_conversazione.replace(/_/g, ' ')}
                </div>
                {task.domanda_pendente && (
                  <div style={{ fontSize: 12, color: 'var(--ds-text)', marginBottom: 6 }}>
                    <strong>Domanda:</strong> {task.domanda_pendente}
                  </div>
                )}
                {task.contesto_conversazione && task.contesto_conversazione.length > 0 && (
                  <div style={{ fontSize: 12, color: 'var(--ds-muted)' }}>
                    {task.contesto_conversazione
                      .filter(s => s.domanda && s.risposta)
                      .map((scambio, i) => (
                        <div key={i} style={{ marginBottom: 4, paddingLeft: 8, borderLeft: '2px solid ' + tint('var(--zone-amber)', 30) }}>
                          <div><strong>Q:</strong> {scambio.domanda}</div>
                          <div><strong>R:</strong> {scambio.risposta}</div>
                        </div>
                      ))
                    }
                  </div>
                )}
                {task.task_padre && (
                  <div style={{ fontSize: 11, color: 'var(--ds-muted)', marginTop: 4 }}>
                    Risposta a task: {task.task_padre.toString().slice(0, 8)}…
                  </div>
                )}
              </div>
            )}

            {/* Rispondi dalla piattaforma */}
            {task.stato === 'in_attesa_risposta' && (
              <RispondiForm taskId={task.id} onSuccess={onRisposta} />
            )}

            {/* Errore */}
            {task.errore && (
              <div style={{
                marginTop: 12, padding: '8px 12px', borderRadius: 'var(--radius-base)',
                background: tint('var(--ds-danger)', 8), border: '1px solid ' + tint('var(--ds-danger)', 25),
                fontSize: 12, color: 'var(--ds-danger)', fontFamily: 'monospace',
              }}>
                {task.errore}
              </div>
            )}

            {/* Dettagli numerici */}
            <div style={{ display: 'flex', gap: 24, marginTop: 12, fontSize: 11, color: 'var(--ds-muted)' }}>
              {task.tokens_usati && <span>Tokens: <strong>{task.tokens_usati}</strong></span>}
              {task.tentativi > 1 && <span style={{ color: 'var(--warn)' }}>Tentativi: <strong>{task.tentativi}</strong></span>}
              {task.azione_eseguita && <span style={{ color: 'var(--brand)' }}>Azione eseguita</span>}
              {task.tipo_messaggio === 'text' && <span style={{ color: 'var(--zone-amber)' }}>Risposta testo</span>}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// ── Pagina principale ─────────────────────────────────────────────────────────

export default function AIMonitor() {
  const isMobile = useIsMobile();
  const location = useLocation();
  const [tasks, setTasks]         = useState([]);
  const [usage, setUsage]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const pollingRef = useRef(null);

  // ── Carica dati ─────────────────────────────────────────────────────────────

  const caricaTasks = useCallback(async (silent = false) => {
    try {
      const r = await aiApi.tasks();
      setTasks(r.data);
      setLastUpdate(new Date());
      if (!silent) setLoading(false);
    } catch {
      if (!silent) { setLoading(false); setError(true); }
    }
  }, []);

  const caricaUsage = useCallback(async () => {
    try {
      const r = await aiApi.usage();
      setUsage(r.data);
    } catch {}
  }, []);

  useEffect(() => {
    Promise.all([caricaTasks(), caricaUsage()]);
  }, [caricaTasks, caricaUsage]);

  // ── Polling automatico quando ci sono task attivi ────────────────────────────

  useEffect(() => {
    const haActive = tasks.some(t => STATI_ATTIVI.includes(t.stato));

    if (haActive) {
      pollingRef.current = setInterval(() => caricaTasks(true), 4000);
    } else {
      clearInterval(pollingRef.current);
    }

    return () => clearInterval(pollingRef.current);
  }, [tasks, caricaTasks]);

  // Deep-link da centro notifiche: /ai-monitor?task={id} espande il task
  useEffect(() => {
    const taskId = new URLSearchParams(location.search).get('task');
    if (taskId) setExpandedId(taskId);
  }, [location.search]);

  // ── KPI calcolati ────────────────────────────────────────────────────────────

  const oggi = new Date().toISOString().slice(0, 10);
  const tasksOggi  = tasks.filter(t => t.created_at?.startsWith(oggi));
  const completati = tasks.filter(t => t.stato === 'completato').length;
  const falliti    = tasks.filter(t => t.stato === 'fallito').length;
  const latenze    = tasks.filter(t => t.latenza_totale_ms).map(t => t.latenza_totale_ms);
  const latenzaMedia = latenze.length ? Math.round(latenze.reduce((a, b) => a + b, 0) / latenze.length) : null;

  const hasActive = tasks.some(t => STATI_ATTIVI.includes(t.stato));

  // ── Render ───────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div style={{ padding: 32 }}>
        <div style={{ height: 28, background: 'var(--ds-border)', borderRadius: 'var(--radius-base)', width: 180, marginBottom: 24 }} className="skeleton" />
        <div style={{ display: 'flex', gap: 16, marginBottom: 24 }}>
          {[1,2,3,4].map(i => <div key={i} style={{ flex: 1, height: 80, background: 'var(--ds-border)', borderRadius: 'var(--radius-card)' }} className="skeleton" />)}
        </div>
        <div style={{ height: 300, background: 'var(--ds-border)', borderRadius: 'var(--radius-card)' }} className="skeleton" />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 32, textAlign: 'center' }}>
        <div style={{ fontSize: 15, fontWeight: 500, color: 'var(--ink)', marginBottom: 8 }}>
          Impossibile caricare i dati AI
        </div>
        <div style={{ fontSize: 13, color: 'var(--ds-muted)', marginBottom: 16 }}>
          Verifica la connessione e riprova.
        </div>
        <button
          onClick={() => { setError(false); setLoading(true); Promise.all([caricaTasks(), caricaUsage()]); }}
          style={{
            padding: '10px 24px', borderRadius: 'var(--radius-base)', border: 'none',
            background: 'var(--brand)', color: '#fff',
            fontSize: 13, fontWeight: 500, cursor: 'pointer',
          }}
        >
          Riprova
        </button>
      </div>
    );
  }

  return (
    <div style={{ padding: isMobile ? '20px 16px' : 32, maxWidth: 1200 }}>

      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8, flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
        <h1 style={{ fontSize: 22, fontWeight: 500, fontFamily: 'var(--font-display)', color: 'var(--ink)', margin: 0 }}>
          AI Monitor
        </h1>
        <span style={{
          padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 500,
          background: tint('var(--brand)', 12), color: 'var(--brand)',
          textTransform: 'uppercase', letterSpacing: '0.08em',
        }}>
          BETA
        </span>
        {hasActive && (
          <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--zone-blue)' }}>
            <span style={{
              width: 8, height: 8, borderRadius: '50%', background: 'var(--zone-blue)',
              animation: 'skeleton-pulse 1.2s ease-in-out infinite',
            }} />
            Pipeline attiva
          </span>
        )}
        <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--ds-muted)' }}>
          {lastUpdate ? `Aggiornato ${lastUpdate.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}` : ''}
          {hasActive && <span style={{ color: 'var(--zone-blue)' }}> · polling 4s</span>}
        </span>
      </div>
      <p style={{ fontSize: 13, color: 'var(--ds-muted)', marginBottom: 28, marginTop: 0 }}>
        Pipeline vocale WhatsApp → Whisper → Claude → DB
      </p>

      {/* ── KPI ── (mobile: 2x2, niente flex-row schiacciata) */}
      <div style={{ display: isMobile ? 'grid' : 'flex', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : undefined, gap: 16, marginBottom: 28 }}>
        <KpiCard
          label="Messaggi oggi"
          value={tasksOggi.length}
          color="var(--ink)"
        />
        <KpiCard
          label="Completati"
          value={completati}
          color="var(--brand)"
          sub={tasks.length > 0 ? `${Math.round(completati / tasks.length * 100)}% successo` : null}
        />
        <KpiCard
          label="Falliti"
          value={falliti}
          color={falliti > 0 ? 'var(--ds-danger)' : 'var(--ds-muted)'}
        />
        <KpiCard
          label="Latenza media"
          value={fmtLatenza(latenzaMedia)}
          color="var(--ink)"
          sub="trascrizione + parsing + azione"
        />
      </div>

      {/* ── Tabella task ── */}
      <div style={{
        background: 'var(--ds-bg)', border: '1px solid var(--ds-border)',
        borderRadius: 'var(--radius-card)', boxShadow: CARD_SHADOW, overflow: 'hidden',
      }}>
        <div style={{
          padding: '14px 20px', borderBottom: '1px solid var(--ds-border)',
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--ink)' }}>
            Ultimi {tasks.length} messaggi vocali
          </span>
          <button
            onClick={async () => { setRefreshing(true); await caricaTasks(); setRefreshing(false); }}
            disabled={refreshing}
            style={{
              marginLeft: 'auto', padding: '8px 16px', borderRadius: 'var(--radius-base)',
              border: '1px solid var(--ds-border)', background: 'transparent',
              fontSize: 12, color: 'var(--ds-muted)', cursor: refreshing ? 'wait' : 'pointer',
              opacity: refreshing ? 0.6 : 1,
            }}
          >
            {refreshing ? 'Aggiornamento...' : 'Aggiorna'}
          </button>
        </div>

        {tasks.length === 0 ? (
          <div style={{ padding: '48px 24px', textAlign: 'center' }}>
            <div style={{ marginBottom: 12 }}><Mic size={32} strokeWidth={1.5} color="var(--ds-muted)" /></div>
            <div style={{ fontSize: 15, fontWeight: 500, color: 'var(--ink)', marginBottom: 6 }}>
              Nessun messaggio vocale ancora
            </div>
            <div style={{ fontSize: 13, color: 'var(--ds-muted)' }}>
              I messaggi WhatsApp dei tuoi utenti appariranno qui
            </div>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: 'var(--chip)', borderBottom: '1px solid var(--ds-border)' }}>
                  {['Stato', 'Intent', 'Trascrizione', 'Utente', 'Latenza', 'Data', ''].map((h, i) => (
                    <th key={i} style={{
                      padding: '8px 12px', textAlign: i === 4 ? 'right' : 'left',
                      fontSize: 11, fontWeight: 500, color: 'var(--ds-muted)',
                      textTransform: 'uppercase', letterSpacing: '0.05em',
                    }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {tasks.map(t => (
                  <TaskRow
                    key={t.id}
                    task={t}
                    expanded={expandedId === t.id}
                    onToggle={() => setExpandedId(expandedId === t.id ? null : t.id)}
                    onRisposta={() => caricaTasks()}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Storico utilizzo mensile ── */}
      {usage.length > 0 && (
        <div style={{
          marginTop: 24, background: 'var(--ds-bg)',
          border: '1px solid var(--ds-border)', borderRadius: 'var(--radius-card)', boxShadow: CARD_SHADOW, overflow: 'hidden',
        }}>
          <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--ds-border)' }}>
            <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--ink)' }}>
              Utilizzo mensile
            </span>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: 'var(--chip)', borderBottom: '1px solid var(--ds-border)' }}>
                  {['Mese', 'Messaggi parsati', 'Tokens'].map((h, i) => (
                    <th key={i} style={{
                      padding: '8px 16px', textAlign: i > 0 ? 'right' : 'left',
                      fontSize: 11, fontWeight: 500, color: 'var(--ds-muted)',
                      textTransform: 'uppercase', letterSpacing: '0.05em',
                    }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {usage.map((u, i) => (
                  <tr key={i} className="table-row">
                    <td style={{ padding: '10px 16px', fontWeight: 500 }}>
                      {new Date(u.anno, u.mese - 1).toLocaleString('it-IT', { month: 'long', year: 'numeric' })}
                    </td>
                    <td style={{ padding: '10px 16px', textAlign: 'right' }}>
                      {u.messaggi_vocali_parsati.toLocaleString()}
                    </td>
                    <td style={{ padding: '10px 16px', textAlign: 'right', color: 'var(--ds-muted)' }}>
                      {u.tokens_totali.toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

    </div>
  );
}
